import { api } from './api';

export interface QuizOptionDTO {
  id: number;
  text: string;
  displayOrder: number;
}

export interface QuizQuestionDTO {
  id: number;
  text: string;
  questionType: string;
  options: QuizOptionDTO[];
}

export interface ActiveQuizDTO {
  quizSessionId: number;
  questions: QuizQuestionDTO[];
  timerSeconds: number;
  remainingSeconds: number;
  status: 'ACTIVE' | 'CLOSED';
}

export interface QuizSubmissionResultDTO {
  quizSessionId: number;
  answers: AnswerResult[];
  score: number;
  totalQuestions: number;
}

export interface AnswerResult {
  questionId: number;
  selectedOptionId: number;
  correct: boolean;
}

export interface SessionStateDTO {
  sessionId: number;
  status: 'ACTIVE' | 'ENDED';
  currentTopicId: number | null;
  currentPageIndex: number;
  lastSeq: number;
  startedAt: string;
  activeQuiz: ActiveQuizDTO | null;
}

export interface ActiveSessionInfoDTO {
  sessionId: number;
  courseId: number;
  startedAt: string;
  tutorName: string;
}

export async function getSessionState(
  courseId: number,
  sinceSeq?: number
): Promise<SessionStateDTO | null> {
  try {
    const params = sinceSeq !== undefined ? { since: sinceSeq } : {};
    const response = await api.get<{ data: SessionStateDTO }>(
      `/api/student/session/state/${courseId}`,
      { params }
    );
    return response.data.data;
  } catch (err: any) {
    const status = err?.response?.status;
    if (status === 204) return null;   // no change
    if (status === 404) return null;   // no active session
    throw err;
  }
}

export async function getActiveSessionInfo(
  courseId: number
): Promise<ActiveSessionInfoDTO | null> {
  try {
    const response = await api.get<{ data: ActiveSessionInfoDTO }>(
      `/api/student/session/active/${courseId}`
    );
    return response.data.data;
  } catch (err: any) {
    if (err?.response?.status === 204 || err?.response?.status === 404) return null;
    throw err;
  }
}

export async function getStudentNotes(sessionId: number): Promise<string> {
  try {
    const response = await api.get<{ data: { content: string } }>(
      `/api/student/session/${sessionId}/notes`
    );
    return response.data.data.content ?? '';
  } catch {
    return '';
  }
}

export async function saveStudentNotes(sessionId: number, content: string): Promise<void> {
  await api.put(`/api/student/session/${sessionId}/notes`, { content });
}

export async function submitQuizAnswers(
  quizSessionId: number,
  answers: { questionId: number; selectedOptionId: number }[]
): Promise<QuizSubmissionResultDTO> {
  const response = await api.post<{ data: QuizSubmissionResultDTO }>(
    `/api/student/session/quiz/${quizSessionId}/submit`,
    { answers }
  );
  return response.data.data;
}
